import { getRequest, patchRequest } from "../../../utils/apiRequest";
import { useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { userState } from "../../../utils/atom";
import { Loader } from "../../../components/loader";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { formatDateTimeToDDMMYY, formatDateTimeToHHMM } from "../../../utils/formatDateTime";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ArrowLeft, ChevronDoubleLeft, ChevronDoubleRight } from "react-bootstrap-icons";
import { CourseListingFilter } from "../../../components/listing/course-listing/course-listing-filter/courseListingFilter";
import { formatWithDots } from "../../../utils/formatNumbers";

export const AllPlannedCourses = () => {
    const { token } = useRecoilValue(userState);
    const [page, setPage] = useState(1);
    const [categories, setCategories] = useState([]);
    const [totalPages, setTotalPages] = useState([]);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [showFullCourse, setShowFullCourse] = useState(true);
    const [courses, setCourses] = useState([]);
    const [coursesPerPage, setCoursesPerPage] = useState(5);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showToast, setShowToast] = useOutletContext();
    const navigate = useNavigate();

    const allPlannedCourses = useQuery({
        queryKey: ["allplannedcourses", coursesPerPage, page, categories, dateTo, dateFrom, showFullCourse],
        queryFn: () =>
            getRequest(
                `/auth/course/planned?page=${page}&amount=${coursesPerPage}${categories
                    .map((cat) => `&category=${cat.id}`)
                    .join("")}${dateFrom ? `&dateFrom=${dateFrom}` : ""}${dateTo ? `&dateTo=${dateTo}` : ""}${
                    !showFullCourse ? `&hidefull=true` : ""
                }`,
                token
            ),
    });
    const { data } = allPlannedCourses;

    const courseTagQuery = useQuery({
        queryKey: ["courseTagQuery"],
        queryFn: () => getRequest("/auth/course/tags", token),
    });
    const { data: courseTags } = courseTagQuery;

    useEffect(() => {
        if (data) {
            const pagesAmount = Math.ceil(data.total / coursesPerPage);
            setTotalPages(pagesAmount);
            setCourses(data.courses);
        }
    }, [data]);

    return (
        <Loader
            isActive={
                allPlannedCourses.isFetching ||
                allPlannedCourses.isLoading ||
                courseTagQuery.isFetching ||
                courseTagQuery.isLoading
            }
        >
            {data && (
                <Container fluid>
                    <Row className="border-0 p-3 bg-dark-subtle border-bottom border-white mb-3">
                        <Col xs={2} className="p-0">
                            <ArrowLeft size={24} onClick={() => navigate(-1)} style={{ cursor: "pointer" }} />
                        </Col>
                        <Col xs={8} className="text-center d-flex align-items-center justify-content-center">
                            <h5 className="m-0">Alle geplanten Kurse</h5>
                        </Col>
                        <Col xs={2}></Col>
                    </Row>

                    <CourseListingFilter
                        categories={categories}
                        setCategories={setCategories}
                        coursesPerPage={coursesPerPage}
                        setCoursesPerPage={setCoursesPerPage}
                        dateFrom={dateFrom}
                        setDateFrom={setDateFrom}
                        dateTo={dateTo}
                        setDateTo={setDateTo}
                        courseTags={courseTags}
                        showDropdown={showDropdown}
                        setShowDropdown={setShowDropdown}
                        showFullCourse={showFullCourse}
                        setShowFullCourse={setShowFullCourse}
                    />

                    {courses.length < 1 ? (
                        <Row className="mt-3">
                            <Col className="text-center">
                                <p>Zu den aktuellen Filtereinstellungen konnten keine Kurse gefunden werden.</p>
                                <Button onClick={() => navigate("/new-course")}>Neuen Kurs planen</Button>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Container>
                                <Row className="mt-3">
                                    {courses.map((course) => {
                                        return (
                                            <Col xs={12} md={6} lg={4} className="mt-3 mb-3">
                                                <Card key={course.id}>
                                                    <Card.Header className="p-3">
                                                        <h5 className="m-0">{course.title}</h5>
                                                    </Card.Header>
                                                    <Card.Body className="p-0">
                                                        <Row className="m-0 p-3 border-bottom">
                                                            <Col className="p-0" xs={3}>
                                                                Datum:
                                                            </Col>
                                                            <Col className="text-end" xs={9}>
                                                                {formatDateTimeToDDMMYY(course.date)}
                                                            </Col>
                                                        </Row>
                                                        <Row className="m-0 p-3 border-bottom">
                                                            <Col className="p-0" xs={3}>
                                                                Uhrzeit:
                                                            </Col>
                                                            <Col className="text-end" xs={9}>
                                                                {formatDateTimeToHHMM(course.date)} Uhr
                                                            </Col>
                                                        </Row>
                                                        <Row className="m-0 p-3 border-bottom">
                                                            <Col className="p-0" xs={3}>
                                                                Ort:
                                                            </Col>
                                                            <Col className="text-end" xs={9}>
                                                                {course.locationName}, {course.locationCity}
                                                            </Col>
                                                        </Row>
                                                        <Row className="m-0 p-3 border-bottom">
                                                            <Col className="p-0" xs={3}>
                                                                Lehrer:
                                                            </Col>
                                                            <Col className="text-end" xs={9}>
                                                                {course.courseTeacher}
                                                            </Col>
                                                        </Row>
                                                        <Row className="m-0 p-3 border-bottom">
                                                            <Col className="p-0" xs={3}>
                                                                Preis:
                                                            </Col>
                                                            <Col className="text-end" xs={9}>
                                                                {course.cost == 0
                                                                    ? "Im Abo enthalten"
                                                                    : formatWithDots(course.cost) + " €"}
                                                            </Col>
                                                        </Row>
                                                        <Row className="m-0 p-3">
                                                            <Col className="p-0" xs={9}>
                                                                Maximale Teilnehmer:
                                                            </Col>
                                                            <Col className="text-end" xs={3}>
                                                                {course.maxParticipants}
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                    <Card.Footer className="p-3">
                                                        <Row>
                                                            <Col xs={6}>
                                                                <Button
                                                                    className="bg-success border-0 w-100"
                                                                    onClick={async () => {
                                                                        await patchRequest(
                                                                            "/auth/course/activate",
                                                                            token,
                                                                            {
                                                                                id: course.id,
                                                                            }
                                                                        );
                                                                        allPlannedCourses.refetch();
                                                                        setShowToast({
                                                                            toastStatus: true,
                                                                            toastMessage:
                                                                                "Kurs erfolgreich freigegeben. 🥳",
                                                                            toastAppearance: "success",
                                                                        });
                                                                    }}
                                                                >
                                                                    Kurs freigeben
                                                                </Button>
                                                            </Col>
                                                            <Col xs={6}>
                                                                <Button
                                                                    className="bg-primary border-0 w-100"
                                                                    onClick={() =>
                                                                        navigate(`/edit-course/${course.id}`)
                                                                    }
                                                                >
                                                                    Kurs bearbeiten
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </Card.Footer>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>

                                {courses.length > 0 && (
                                    <Row>
                                        <Col xs={2} className="d-flex align-items-center">
                                            {page > 1 && (
                                                <ChevronDoubleLeft
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setPage((prev) => prev - 1)}
                                                />
                                            )}
                                        </Col>
                                        <Col xs={8} className="mb-3 mt-3">
                                            <Form.Select
                                                value={page}
                                                onChange={(e) => setPage(parseInt(e.target.value))}
                                            >
                                                {Array.from({ length: totalPages }, (_, index) => (
                                                    <option className="text-center" key={index + 1} value={index + 1}>
                                                        {`Seite ${index + 1} von ${totalPages}`}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>

                                        <Col xs={2} className="d-flex align-items-center justify-content-end">
                                            {page < totalPages && (
                                                <ChevronDoubleRight
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setPage((prev) => prev + 1)}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                )}
                            </Container>
                        </>
                    )}
                </Container>
            )}
        </Loader>
    );
};
